import React, { useEffect, useRef, useState } from "react";

/**
 * Scroll-reactive word strip. It drifts on its own; scrolling speeds it up and
 * skews it with the scroll velocity, and scrolling back up reverses it. Hovering
 * eases it to a stop. Decorative (aria-hidden); static under reduced motion.
 */
const Ticker = ({ words }) => {
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  // The words are set in a web font; stay hidden until it is ready so the swap cannot shift the layout.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let live = true;
    const done = () => live && setReady(true);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(done);
    else done();
    const t = window.setTimeout(done, 2500);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const track = trackRef.current;
    if (!root || !track) return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    let x = 0;
    let dir = 1;
    let boost = 0;
    let skew = 0;
    let hover = false;
    let lastY = window.scrollY;
    let last = performance.now();
    let raf = 0;
    let setWidth = 0;

    const measure = () => {
      const first = track.firstElementChild;
      setWidth = first ? first.getBoundingClientRect().width : 0;
    };
    measure();

    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      lastY = y;
      if (Math.abs(delta) > 0.5) dir = delta > 0 ? 1 : -1;
      boost = Math.min(Math.abs(delta) * 0.9, 38);
    };

    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      boost *= 0.92;
      const base = hover ? 0 : 70;
      x -= dir * (base + boost * 60) * dt;
      if (setWidth > 0) {
        if (x <= -setWidth) x += setWidth;
        if (x > 0) x -= setWidth;
      }
      const targetSkew = hover ? 0 : -dir * Math.min(boost * 0.35, 9);
      skew += (targetSkew - skew) * 0.12;
      track.style.transform = `translate3d(${x}px,0,0) skewX(${skew}deg)`;
      raf = requestAnimationFrame(tick);
    };

    const enter = () => { hover = true; };
    const leave = () => { hover = false; };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    root.addEventListener("pointerenter", enter);
    root.addEventListener("pointerleave", leave);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      root.removeEventListener("pointerenter", enter);
      root.removeEventListener("pointerleave", leave);
    };
  }, []);

  // three identical sets so the loop never shows a gap, even on very wide screens
  return (
    <div className="ticker" ref={rootRef} aria-hidden="true" data-ready={ready}>
      <div className="ticker__track" ref={trackRef}>
        {[0, 1, 2].map((copy) => (
          <ul key={copy} className="ticker__set">
            {words.map((word, i) => (
              <li key={word} className={i % 2 ? "is-outline" : "is-solid"}>
                <span className="ticker__idx">{String(i + 1).padStart(2, "0")}</span>
                <span className="ticker__word">{word}</span>
                <span className="ticker__mark" />
              </li>
            ))}
          </ul>
        ))}
      </div>
    </div>
  );
};

export default Ticker;
